/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import Timer from 'react-compound-timer';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';

import { countryListNotValidForOTP } from 'components/Auth/Signup/constants';
import OTPInput from 'components/OTPVerification';
import Render from 'components/render';
import { OTP_LENGTH } from 'constants/constants';
import { isEmpty } from 'helpers/ramda';
import {
  noop,
  parseErrorMsg,
  validateIndianPhoneNumber,
  validatePhoneNumber,
} from 'helpers/utils';
import i18n from 'i18n/config';
import {
  getUserId,
  selectUserCountry,
  isUserIndianSelector,
} from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import { CrossIcon } from 'styles/assets/icons';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';
import IconButton from 'UIKit/IconButton';

import OtpSuccessModal from './OtpSuccessModal';
import classes from './style.module.scss';

interface AddMobileNumberModalProps {
  open: boolean;
  closeModal: () => void;
  value: string;
  isdCode: string;
  addPhoneNumber: (payload) => void;
  resendPhoneNumberOTP: (payload) => void;
  verifyMobileNumber: (payload) => void;
}

const formatCountryCode = (countryCodeValue: string) => {
  const code = countryCodeValue.split('+')[1];
  return code.includes('-') ? code.replace('-', '') : code.replace(' ', '');
};

const phoneNumberIsInvalidErrorMessage = i18n.t('errors:user.invalid_phone_number');
const phoneNumberEmptyErrorMessage = i18n.t('errors:user.empty_phone_number');

const AddMobileNumberModal = ({
  open,
  closeModal,
  value,
  isdCode,
  addPhoneNumber,
  resendPhoneNumberOTP,
  verifyMobileNumber,
}: AddMobileNumberModalProps) => {
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOTP] = useState('');
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [otpError, showOTPError] = useState<string>('');
  const [inputError, showInputError] = useState<string>('');
  const [attemptLimitReached, setAttemptLimitReached] = useState(false);
  const [resendOTPSuccess, setResendOTPSuccess] = useState(false);
  const [disabledOTPResend, setDisabledOTPResend] = useState(true);
  const [phoneVerificationToken, setPhoneVerificationToken] = useState<string>();
  const timerRef = useRef(null);

  const { t } = useTranslation(['account', 'common', 'errors', 'auth']);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const userCountry = useAppSelector(selectUserCountry);
  const isUserIndian = useAppSelector(isUserIndianSelector);
  const userId = useAppSelector(getUserId);

  useEffect(() => {
    setPhoneNumber(!value ? '' : String(value));
  }, [value, open]);

  // eslint-disable-next-line consistent-return
  const getVerificationOtp = async () => {
    setLoading(true);
    const payload = {
      user_id: Number(userId),
      phone_number: Number(phoneNumber),
      country_calling_code: Number(formatCountryCode(isdCode)),
    };

    try {
      const isCountryNotValidForOTP = countryListNotValidForOTP.some(
        item => item.label === userCountry
      );
      if (isCountryNotValidForOTP) {
        return showInputError(t('errors:user.otp_sms_service_unavailable'));
      }
      const response = await addPhoneNumber(payload);
      const token = response?.result;
      setPhoneVerificationToken(token);
      setShowOTPInput(true);
    } catch (errorResponse) {
      const { error } = parseErrorMsg(errorResponse);
      if (error?.code === 'api_empty_response') {
        showInputError(t('errors:user.ip_api_error'));
      }
      if (error?.code === 'mobile_otp_blocked') {
        showInputError(t('errors:user.mobile_otp_blocked'));
      }
      if (error?.code === 'invalid_phone_number_format') {
        showInputError(t('errors:user.invalid_phone_number_format'));
      }
      if (error?.code === 'duplicate_phone_number_found') {
        showInputError(t('errors:user.duplicate_phone_number'));
      }
      if (error?.code === 'message_bird_limit_reached') {
        showInputError(t('errors:user.message_bird_limit_reached'));
      }
      if (error?.code === 'blocked_country_calling_code') {
        showInputError(t('errors:user.blocked_country_calling_code'));
      }
      if (error?.code === 'failed_to_generate_or_send_otp') {
        showInputError(t('errors:user.failed_to_generate_or_send_otp'));
      }
      if (error?.code === 'already_verified') {
        showInputError(t('errors:user.already_verified'));
      }
      if (error?.code === 'verify_phone_number_blocked') {
        showInputError(t('errors:user.verify_phone_number_blocked'));
      }
      if (error?.code === 'duplicate_phone_number_found_in_deleted_account') {
        showInputError(t('errors:user.duplicate_phone_number_found_in_deleted_account'));
      }
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationOTP = async (startTimer, resetTimer) => {
    if (!timerRef.current) {
      timerRef.current = { startTimer, resetTimer };
    }
    setLoading(true);
    showOTPError('');
    const payload = {
      phone_verification_jwt_token: phoneVerificationToken,
    };

    try {
      await resendPhoneNumberOTP(payload);
      resetTimer();
      startTimer();
      setResendOTPSuccess(true);
    } catch (errorResponse) {
      const { error } = parseErrorMsg(errorResponse);
      if (error.code === 'api_empty_response')
        showOTPError(t('errors:user.ip_api_error'));

      if (error.code === 'mobile_otp_blocked') {
        showOTPError(t('errors:user.mobile_otp_blocked'));
        setAttemptLimitReached(true);
      }
      if (error.code === 'invalid_phone_number_format') {
        showOTPError(t('errors:user.invalid_phone_number_format'));
        setAttemptLimitReached(true);
      }
      if (error?.code === 'duplicate_phone_number_found') {
        showInputError(t('errors:user.duplicate_phone_number'));
      }
      if (error.code === 'message_bird_limit_reached') {
        showOTPError(t('errors:user.message_bird_limit_reached'));
        setAttemptLimitReached(true);
      }
      if (error?.code === 'blocked_country_calling_code') {
        showInputError(t('errors:user.blocked_country_calling_code'));
      }
      if (error?.code === 'failed_to_generate_or_send_otp') {
        showInputError(t('errors:user.failed_to_generate_or_send_otp'));
      }
      if (error?.code === 'already_verified') {
        showInputError(t('errors:user.already_verified'));
      }
      if (error?.code === 'verify_phone_number_blocked') {
        showInputError(t('errors:user.verify_phone_number_blocked'));
      }
      if (error?.code === 'duplicate_phone_number_found_in_deleted_account') {
        showInputError(t('errors:user.duplicate_phone_number_found_in_deleted_account'));
      }
      setResendOTPSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const showSuccessPopupHandle = () => {
    setShowSuccessPopup(true);
  };

  const submitVerificationOtp = async () => {
    setLoading(true);
    setResendOTPSuccess(false);
    const payload = {
      phone_verification_jwt_token: phoneVerificationToken,
      otp,
    };

    try {
      await verifyMobileNumber(payload);
      setShowOTPInput(false);
      closeModal();
      showSuccessPopupHandle(); // Show success pop-up
    } catch (errorResponse) {
      const { error } = parseErrorMsg(errorResponse);
      if (error.code === 'api_empty_response') {
        showOTPError(t('errors:user.ip_api_error'));
      }
      if (error.code === 'invalid_otp') {
        const { count = 0, max_attempts: maxAttempts = 5 } = error.context || {};
        const attemptsRemaining = maxAttempts - count;
        let errorMsg = t('errors:custom.invalid_otp_with_count_plural', {
          count: attemptsRemaining,
        });
        if (attemptsRemaining === 1) errorMsg = t('errors:custom.invalid_otp_with_count');
        showOTPError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    showInputError('');
    const { value } = e.target;
    if (value === '') {
      setPhoneNumber(value);
      return;
    }
    const phoneNumberPattern = /^[0-9]+$/;
    if (!phoneNumberPattern.test(value)) return;
    setPhoneNumber(value);
  };

  const handleOTPChange = (otpValue: string) => {
    setOTP(otpValue);
  };

  const resetValues = () => {
    setPhoneNumber('');
    setLoading(false);
    setShowOTPInput(false);
    showInputError('');
    showOTPError('');
  };

  const exitModal = () => {
    resetValues();
    closeModal();
  };

  const enableResendOTP = () => {
    setDisabledOTPResend(false);
  };

  const handleNumberSubmitClick = () => {
    showInputError('');

    if (isEmpty(phoneNumber)) {
      showInputError(phoneNumberEmptyErrorMessage);
      return;
    }

    const isPhoneNumberValid = isUserIndian
      ? validateIndianPhoneNumber(phoneNumber)
      : validatePhoneNumber(phoneNumber);

    if (!isPhoneNumberValid) {
      showInputError(phoneNumberIsInvalidErrorMessage);
      return;
    }

    getVerificationOtp();
  };

  return (<>
    <Modal
      className={classes.kycModal}
      isOpen={open}
      overlayClassName={classes.overlay}
    >
      <>
        <div className={classes.heading} data-palette="AddMobileNumberModal">
          <h2>
            {t('account:profile.addMobileNumber')}
            <IconButton
              onClick={exitModal}
              data-testid="closeModalButton"
              className={classes.closeModalButton}
              icon={<CrossIcon />}
              onKeyDown={noop}
            />
          </h2>
        </div>
        <Box flex="flex" flexDir="column" className={classes.main}>
          {!showOTPInput ? (
            <div data-palette="AddMobileNumberModal">
              <label className={classes.phoneNumberLabel} htmlFor="phoneNumberInput">
                {t('account:profile.mobileNo')}
              </label>
              <div
                className={cx(classes.phoneNumberInputCont, {
                  [classes.erroredInput]: inputError,
                })}
              >
                <div className={classes.isdCode}>{isdCode}</div>
                <div className={classes.inputWrapper}>
                  <input
                    inputMode="numeric"
                    autoFocus
                    type="text"
                    data-testid="phoneNumberInput"
                    onChange={handleChange}
                    value={phoneNumber}
                    className={classes.phoneNumberInput}
                  />
                </div>
              </div>
              {Boolean(inputError) && (
                <div className={classes.errorMessage} data-palette="AddMobileNumberModal">{inputError}</div>
              )}
            </div>
          ) : (
            <div data-palette="AddMobileNumberModal">
              <label className={classes.phoneNumberLabel}>
                {t('account:security.email_otp_label', {
                  targetAddress: phoneNumber,
                })}
              </label>
              <OTPInput
                autoFocus
                isNumberInput
                length={OTP_LENGTH}
                className={classes.otpContainer}
                hideSendCodeRightSection
                inputClassName={cx(classes.otpInput, {
                  [classes.invalid]: Boolean(otpError),
                })}
                onChangeOTP={handleOTPChange}
                testID="addMobileNumber-otpInput"
              />
              <div>
                <Box className={classes.errorMessage} renderWhen={Boolean(otpError)}>
                  {otpError}
                </Box>
                <Box
                  className={classes.successMessage}
                  renderWhen={Boolean(resendOTPSuccess)}
                >
                  {t('auth:otpSent')}
                </Box>
                <Box
                  className={classes.resendCodeSectionClasses}
                  renderWhen={!attemptLimitReached}
                >
                  <Timer
                    initialTime={30000}
                    direction="backward"
                    checkpoints={[
                      {
                        time: 0,
                        callback: enableResendOTP,
                      },
                    ]}
                  >
                    {({ start, reset, getTime }) => (
                      <>
                        {disabledOTPResend && (
                          <span className={classes.timer} data-palette="AddMobileNumberModal">
                            {Math.round(getTime() / 1000)}s
                          </span>
                        )}
                        <button
                          type="button"
                          className={classes.resendCodeBtn}
                          disabled={disabledOTPResend}
                          onClick={() => resendVerificationOTP(start, reset)}
                          data-testid="otpResendButton"
                          data-palette="AddMobileNumberModal">
                          {t('common:resendCode')}
                        </button>
                      </>
                    )}
                  </Timer>
                </Box>
              </div>
            </div>
          )}
        </Box>

        <div className={classes.btnContainer} data-palette="AddMobileNumberModal">
          <Render when={showOTPInput}>
            <DeltaButton
              className={classes.verifyBtn}
              onClick={submitVerificationOtp}
              disabled={loading || isEmpty(otp) || otp.length !== 6}
              data-testid="submitOtpButton"
              text={t('account:security.submit')}
            />
          </Render>
          <Render when={!showOTPInput}>
            <DeltaButton
              className={classes.verifyBtn}
              onClick={handleNumberSubmitClick}
              loading={loading}
              data-testid="getOtpButton"
              text={t('account:profile.getOtp')}
            />
          </Render>
        </div>
      </>
    </Modal>
    <OtpSuccessModal
      onClose={() => setShowSuccessPopup(false)}
      open={showSuccessPopup}
    />
  </>);
};

export default AddMobileNumberModal;
